import { rewriteImageUrl } from './imageHost';

/**
 * Recortes de variantes de imagen sobre la respuesta de Sharetribe (o del espejo).
 *
 * Flex aplica `fields.image` a TODAS las imágenes de la respuesta, así que una variante que
 * necesita una sola de ellas viaja en todas. Y una URL que viaja es una URL que alguien
 * puede pedir: la primera descarga de **cualquier** variante de una foto cuesta los 340
 * créditos de esa foto ese mes, y es un umbral, no un descuento (da igual que R2 tenga las
 * otras veintiséis combinaciones). Ver docs/sharetribe-credits.md §4.2.3.1.
 */

/**
 * Las variantes de un recurso, servidas por nuestro host.
 *
 * Se hace al ENTRAR al almacén, no al pintar, porque lo que el servidor manda en el HTML es
 * el almacén: `window.__PRELOADED_STATE__` llevaba 696 URLs crudas de Sharetribe por página
 * de búsqueda, 144 por ficha y 98 de assets de Console en la portada. Cualquiera que lea ese
 * JSON —y los rastreadores lo leen— tiene la lista exacta de URLs de Sharetribe de cada foto,
 * y cada una que piden son 340 créditos de esa foto ese mes **sin que el Worker la vea**: ni
 * el borde ni R2 la pueden abaratar. Medido el 1-11-2026: un ~27% de las imágenes que
 * Sharetribe entregó no pasó por nuestro CDN.
 *
 * Solo se copia lo que cambia, para no tocar los `UUID`, `Money` y `LatLng` del SDK que
 * viajan en la misma respuesta.
 */
const conNuestroHost = recurso => {
  const variants = recurso?.attributes?.variants;
  if (!variants) {
    return recurso;
  }
  let changed = false;
  const nuevas = {};
  Object.keys(variants).forEach(name => {
    const variante = variants[name];
    const url = rewriteImageUrl(variante?.url);
    if (url !== variante?.url) {
      changed = true;
      nuevas[name] = { ...variante, url };
    } else {
      nuevas[name] = variante;
    }
  });
  return changed
    ? { ...recurso, attributes: { ...recurso.attributes, variants: nuevas } }
    : recurso;
};

/** Una lista de recursos de la API con sus variantes servidas por nuestro host. */
export const resourcesWithOurImageHost = recursos => {
  if (!Array.isArray(recursos)) {
    return recursos;
  }
  const nuevos = recursos.map(conNuestroHost);
  return nuevos.some((r, i) => r !== recursos[i]) ? nuevos : recursos;
};

/** Una respuesta de la API (`{ data, included }`) con sus variantes por nuestro host. */
export const responseWithOurImageHost = apiResponse => {
  if (!apiResponse || typeof apiResponse !== 'object') {
    return apiResponse;
  }
  const { data, included } = apiResponse;
  const dataNueva = Array.isArray(data) ? resourcesWithOurImageHost(data) : conNuestroHost(data);
  const includedNuevo = resourcesWithOurImageHost(included);
  return dataNueva === data && includedNuevo === included
    ? apiResponse
    : { ...apiResponse, data: dataNueva, included: includedNuevo };
};

/**
 * Quita las variantes cuadradas de las fotos de anuncio, no del avatar.
 *
 * `square-small` y `square-small2x` hay que pedirlas —el avatar del autor las necesita, en
 * la tarjeta del buscador y en la ficha—, pero las únicas cosas que las dibujan son
 * `Avatar` y `SuperhostBadge`, y siempre sobre la foto de perfil. En las fotos de anuncio
 * no las dibuja nadie.
 *
 * Medido el 2-11-2026: de las 2.040 URLs de una carga de `/s`, 680 eran estas. Y en
 * `wrangler tail`, 70 de los 78 viajes al origen de una ventana de 300 peticiones eran
 * recortes de 240×240, que es justo `square-small`.
 */
export const withoutSquareVariantsOnListingImages = response => {
  const included = response?.data?.included;
  if (!Array.isArray(included)) {
    return response;
  }

  const avatarIds = new Set();
  included.forEach(r => {
    const id = r?.relationships?.profileImage?.data?.id?.uuid;
    if (id) {
      avatarIds.add(id);
    }
  });

  let changed = false;
  const cleaned = included.map(r => {
    if (r?.type !== 'image' || avatarIds.has(r?.id?.uuid)) {
      return r;
    }
    const variants = r?.attributes?.variants;
    if (!variants) {
      return r;
    }
    const kept = {};
    Object.keys(variants).forEach(name => {
      if (!name.startsWith('square-')) {
        kept[name] = variants[name];
      }
    });
    if (Object.keys(kept).length === Object.keys(variants).length) {
      return r;
    }
    changed = true;
    return { ...r, attributes: { ...r.attributes, variants: kept } };
  });

  return changed ? { ...response, data: { ...response.data, included: cleaned } } : response;
};
